import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { createFullMixEnhanced, createMixEnhancerPreview, deleteTrackStem, renameTrackStem, retrieveTrackStems, searchStemsLibrary } from "../../api/urstudio/mixenhancer.api";
import { showToast } from "../../helper/ShowToast";
import { getUrStudioQueryScope } from "./urstudioScope";

export const useCreateMixEnhnacePreview = () => {
  return useMutation({
    mutationFn: createMixEnhancerPreview,
    onSuccess: () => {
      showToast("success", "Mix Enhanced Preview created");
    },
    onError: (err) =>
      showToast(
        "error",
        err?.response?.data?.message || "Failed to create mix enhanced preview"
      ),
  });
};

export const useCreateFullMixEnhanced = () => {
  return useMutation({
    mutationFn: createFullMixEnhanced,
    onSuccess: () => {
      showToast("success", "Full Mix Enhanced created");
    },
    onError: (err) =>
      showToast(
        "error",
        err?.response?.data?.message || "Failed to create full mix enhanced"
      ),
  });
};

//Stems Queries
export const useRetreiveStemsByTrack = (id) => {
  const access = useSelector((state) => state.auth?.access);
  const scope = getUrStudioQueryScope(access);

  return useQuery({
    queryKey: ['track-stems', scope, id],
    queryFn: () => retrieveTrackStems(id),
    enabled: !!id,  // only runs when id is available
    onError: (err) =>
      showToast(
        "error",
        err?.response?.data?.message || "Failed to fetch mix analysis report"
      ),
  });
}

export const useDeleteStem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => deleteTrackStem({ id }),
    onSuccess: () => {
      // refresh stems library and search results
      queryClient.invalidateQueries(["track-stems"]);
      queryClient.invalidateQueries(["search-stems-library"]);
      showToast("success", "Stem deleted successfully");
    },
    onError: (err) =>
      showToast(
        "error",
        err?.response?.data?.message || "Failed to delete stem"
      ),
  });
};

export const useRenameStem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, body }) => renameTrackStem(id, body),
    onSuccess: () => {
      // refresh stems library and search results
      queryClient.invalidateQueries(["track-stems"]);
      queryClient.invalidateQueries(["search-stems-library"]);
      showToast("success", "Stem renamed");
    },
    onError: (err) =>
      showToast(
        "error",
        err?.response?.data?.message || "Failed to rename mixed track"
      ),
  });
};

export const useSearchStemsLibrary = (qParam) => {
  // accept either a string or an object: useSearchStemsLibrary('foo') or useSearchStemsLibrary({ id: '...', q: 'foo' })
  const query = qParam && typeof qParam === 'object' ? qParam : { q: qParam };
  const access = useSelector((state) => state.auth?.access);
  const scope = getUrStudioQueryScope(access);

  return useQuery({
    queryKey: ["search-stems-library", scope, query.id, query.q],
    queryFn: () => searchStemsLibrary({ id: query.id, q: query.q }),
    enabled: Boolean(query.q), // only run when a query is provided
    keepPreviousData: true, // for smooth pagination
    staleTime: 60 * 1000,
    retry: false,
    onError: (error) => {
      showToast(
        "error",
        error?.response?.data?.message || "Failed to load mixes library"
      );
    },
  });
}
